import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Pagination from 'react-bootstrap/Pagination';
import Spinner from 'react-bootstrap/Spinner';
import Stack from 'react-bootstrap/Stack';
import Table from 'react-bootstrap/Table';
import { Link } from '@tanstack/react-location';
import { useFailedInvoicesQuery } from '../../queries/payment.query';

function FailedInvoicesSection() {
  const [minFailedAttempts, setMinFailedAttempts] = useState(3);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading } = useFailedInvoicesQuery({
    minFailedAttempts,
    page,
    pageSize
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleThresholdChange = (value: number) => {
    setMinFailedAttempts(value);
    setPage(1);
  };

  return (
    <Card>
      <Card.Header>
        <Stack direction="horizontal" className="justify-content-between">
          <h5 className="mb-0">Failed Invoices</h5>
          <Stack direction="horizontal" gap={2}>
            <span className="small text-muted">Threshold:</span>
            <Form.Select
              size="sm"
              style={{ width: 'auto' }}
              value={minFailedAttempts}
              onChange={(e) => handleThresholdChange(parseInt(e.target.value, 10))}>
              <option value={2}>2+ failures</option>
              <option value={3}>3+ failures</option>
              <option value={5}>5+ failures</option>
              <option value={10}>10+ failures</option>
            </Form.Select>
          </Stack>
        </Stack>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Failed Attempts</th>
                  <th>Last Attempt</th>
                </tr>
              </thead>
              <tbody>
                {data?.failedInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.freshbooksId}</td>
                    <td>
                      <Link to={`/clients/${invoice.clientId}`} className="text-decoration-none">
                        {invoice.clientName}
                      </Link>
                    </td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>
                      <Badge bg="danger">{invoice.failedAttemptCount}</Badge>
                    </td>
                    <td>{formatDate(invoice.lastAttemptDate)}</td>
                  </tr>
                ))}
                {data?.failedInvoices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No invoices with {minFailedAttempts}+ failed attempts
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {data && data.numberOfPages > 1 && (
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing page {data.currentPage} of {data.numberOfPages} ({data.totalCount} total)
                </small>
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setPage(1)} disabled={data.currentPage === 1} />
                  <Pagination.Prev
                    onClick={() => setPage(page - 1)}
                    disabled={data.currentPage === 1}
                  />
                  {(() => {
                    const pages: number[] = [];
                    const maxVisible = 5;
                    let start = Math.max(1, data.currentPage - Math.floor(maxVisible / 2));
                    const end = Math.min(data.numberOfPages, start + maxVisible - 1);
                    start = Math.max(1, end - maxVisible + 1);
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    return pages.map((pageNum) => (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === data.currentPage}
                        onClick={() => setPage(pageNum)}>
                        {pageNum}
                      </Pagination.Item>
                    ));
                  })()}
                  <Pagination.Next
                    onClick={() => setPage(page + 1)}
                    disabled={data.currentPage === data.numberOfPages}
                  />
                  <Pagination.Last
                    onClick={() => setPage(data.numberOfPages)}
                    disabled={data.currentPage === data.numberOfPages}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default FailedInvoicesSection;
